import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Loader2, Check, ArrowLeft, Sparkles, Trash2, ChevronRight, Home, Mic, Square, Image as ImageIcon, ListOrdered, GripVertical, Wand2, MapPin, AlertTriangle } from "lucide-react";
import { useAuth, authApi, uploadMemberFile } from "./authApi";
import { API } from "./api";
import { VoiceTextBox } from "./AIAssist";
import { suggestionsForRoom } from "./stagingSuggestions";
import { SoundWave } from "./SoundWave";
import { GoldWave } from "./EspaceAcquereur";
import { OneClickGenerate } from "./OneClickGenerate";
import { CityAutocomplete } from "./CityAutocomplete";
import { compressImage } from "./imageCompress";

const DRAFT_KEY = "visiteia_draft_v1";

const ROOMS = [
  ["facade", "Façade"], ["entree", "Entrée"], ["cuisine", "Cuisine"], ["salon", "Salon"],
  ["salle_a_manger", "Salle à manger"], ["chambre", "Chambre"], ["salle_de_bain", "Salle de bain"],
  ["wc", "WC"], ["bureau", "Bureau"], ["garage", "Garage"], ["cave", "Cave"], ["jardin", "Jardin"],
  ["terrasse", "Terrasse"], ["piscine", "Piscine"], ["dependance", "Dépendance"],
  ["vue_exterieure", "Vue extérieure"], ["autre", "Autre"],
];
const roomLabel = (k) => (ROOMS.find(([rk]) => rk === k) || [null, "Photo"])[1];

const CUSTOM_ROOMS_KEY = "visiteia_custom_rooms";
const loadCustomRooms = () => { try { return JSON.parse(localStorage.getItem(CUSTOM_ROOMS_KEY) || "[]"); } catch { return []; } };

// Ordre logique d'une galerie immobilière (miroir de _ROOM_ORDER côté backend) :
// façade → extérieur → entrée → séjour → cuisine → chambres → sanitaires → extérieurs → annexes.
const GALLERY_ORDER = ["facade", "vue_exterieure", "entree", "salon", "salle_a_manger", "cuisine",
  "chambre", "bureau", "salle_de_bain", "wc", "terrasse", "jardin", "piscine",
  "garage", "cave", "dependance", "autre"];
const roomRank = (r) => { const i = GALLERY_ORDER.indexOf(r); return i === -1 ? GALLERY_ORDER.length : i; };
// Tri STABLE par pièce ; les photos non encore classées restent en fin (dans leur ordre d'ajout).
const sortByRoom = (list) => list
  .map((c, idx) => [c, idx])
  .sort((a, b) => {
    const ra = a[0].room ? roomRank(a[0].room) : 999;
    const rb = b[0].room ? roomRank(b[0].room) : 999;
    return ra !== rb ? ra - rb : a[1] - b[1];
  })
  .map((x) => x[0]);

export default function VisiteIA() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState({ city: "", postal_code: "", department: "", region: "", address: "", price: "", type: "Maison" });
  const [captures, setCaptures] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null); // index en cours d'étiquetage
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [autoSort, setAutoSort] = useState(true); // false dès qu'on réordonne manuellement
  const autoSortRef = useRef(true);
  useEffect(() => { autoSortRef.current = autoSort; }, [autoSort]);
  const [customRooms, setCustomRooms] = useState(loadCustomRooms);
  const saveCustomRoom = (name) => {
    const v = (name || "").trim();
    if (!v) return;
    setCustomRooms((prev) => {
      const next = [v, ...prev.filter((r) => r.toLowerCase() !== v.toLowerCase())].slice(0, 10);
      try { localStorage.setItem(CUSTOM_ROOMS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [genFailed, setGenFailed] = useState(false);
  const [noCredit, setNoCredit] = useState(false);
  const [styles, setStyles] = useState([]);
  const [stStyle, setStStyle] = useState("auto");
  const [customStyle, setCustomStyle] = useState("");
  const [staging, setStaging] = useState(null);
  // Validation phonétique de la ville AVANT génération IA (confirmation si doute).
  const [checking, setChecking] = useState(false);
  const [cityModal, setCityModal] = useState(null); // { suggestions, input, detected }
  // Enregistrement continu de la visite (Lot B)
  const [consent, setConsent] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Restauration d'un brouillon (aucune perte de données après un refresh / échec de génération).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.meta) setMeta(d.meta);
        if (Array.isArray(d.captures)) setCaptures(sortByRoom(d.captures));
        if (d.transcript) setTranscript(d.transcript);
      }
    } catch (_) {}
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sauvegarde continue du brouillon (seulement APRÈS restauration, pour ne pas écraser avec l'état vide).
  useEffect(() => {
    if (!hydrated || result) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ meta, captures, transcript }));
    } catch (_) {}
  }, [meta, captures, transcript, result, hydrated]);

  if (user === false) { nav("/connexion"); return null; }

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 25 * 1024 * 1024) { setErr("Enregistrement trop long (max ~25 Mo). Découpez la visite en plusieurs enregistrements."); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "visite.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd, credentials: "include" });
          if (!res.ok) throw new Error();
          const d = await res.json();
          setTranscript((t) => (t ? t + "\n" : "") + (d.text || ""));
        } catch (_) { setErr("Transcription impossible, réessayez."); }
        setTranscribing(false);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true); setRecSecs(0);
      timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch (_) { setErr("Micro indisponible. Autorisez l'accès au micro."); }
  };
  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };
  const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setErr("");
    const added = [];
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        const d = await uploadMemberFile(compressed);
        added.push({ image_url: d.url, room: "", note: "" });
      } catch (_) { setErr("Une photo n'a pas pu être envoyée. Vérifiez votre connexion et réessayez."); }
    }
    setUploading(false);
    e.target.value = "";
    if (added.length) {
      setCaptures((p) => [...p, ...added]);
      setActive(captures.length + added.length - 1);
      // Détection auto de la pièce (évite un clic) — non bloquant, met à jour dès la réponse.
      // NB : pas de re-tri en direct (sinon la liste saute pendant l'étiquetage).
      added.forEach((cap) => {
        authApi.classifyRoom(cap.image_url).then((d) => {
          if (d && d.room) {
            setCaptures((p) => p.map((c) => (c.image_url === cap.image_url && !c.room ? { ...c, room: d.room } : c)));
          }
        }).catch(() => {});
      });
    }
  };

  const setRoom = (i, room) => setCaptures((p) => p.map((c, j) => (j === i ? { ...c, room } : c)));
  const setNote = (i, note) => setCaptures((p) => p.map((c, j) => (j === i ? { ...c, note } : c)));
  const setStagingNote = (i, staging_note) => setCaptures((p) => p.map((c, j) => (j === i ? { ...c, staging_note } : c)));
  const setRoomCustom = (i, room_custom) => setCaptures((p) => p.map((c, j) => (j === i ? { ...c, room_custom } : c)));
  const addStagingSuggestion = (i, s) => setCaptures((p) => p.map((c, j) => (j === i ? { ...c, staging_note: [c.staging_note, s].filter(Boolean).join(", ") } : c)));
  const removeCap = (i) => setCaptures((p) => p.filter((_, j) => j !== i));
  // Réorganisation manuelle (glisser-déposer) : désactive le tri auto pour préserver l'ordre choisi.
  const dropAt = (to) => {
    setCaptures((p) => {
      if (dragIdx === null || dragIdx === to) return p;
      const a = [...p]; const [it] = a.splice(dragIdx, 1); a.splice(to, 0, it); return a;
    });
    setAutoSort(false); setDragIdx(null); setOverIdx(null);
  };

  const generate = async (override) => {
    const mm = { ...meta, ...(override || {}) };
    if (!captures.length) { setErr("Prenez au moins une photo."); return; }
    setGenerating(true); setErr(""); setGenFailed(false); setNoCredit(false);
    // Sauvegarde des saisies AVANT génération — rien n'est perdu si l'IA échoue.
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ meta: mm, captures, transcript })); } catch (_) {}
    try {
      // Tri par pièce appliqué une seule fois, au moment d'envoyer (pas pendant l'édition → aucun saut).
      const ordered = autoSortRef.current ? sortByRoom(captures) : captures;
      const r = await authApi.visitGenerate({
        captures: ordered.map((c) => ({ image_url: c.image_url, room: (c.room === "autre" && c.room_custom) ? c.room_custom : (c.room || "autre"), note: c.note || "", staging_note: c.staging_note || "" })),
        city: mm.city, postal_code: mm.postal_code, department: mm.department, region: mm.region,
        address: mm.address, type: mm.type,
        price: Number(mm.price) || 0, transcript,
      });
      setResult(r);
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      authApi.listStagingStyles().then((d) => setStyles(d.styles || [])).catch(() => {});
    } catch (e) {
      setGenFailed(true);
      if (e.status === 402 || /crédit|credit|solde|insuffisant/i.test(e.message || "")) {
        setNoCredit(true);
        setErr("Crédits insuffisants pour générer cette annonce. Vos informations sont conservées — rechargez vos crédits pour continuer.");
      } else {
        setErr("La création de votre annonce a rencontré un problème. Vos informations sont sauvegardées — vous pouvez réessayer.");
      }
    }
    setGenerating(false);
  };

  // Pré-vérification : valide phonétiquement la ville (ou l'extrait de la dictée si le champ est vide).
  // En cas de doute → modale de confirmation ; sinon → génération directe.
  const preflight = async () => {
    if (!captures.length) { setErr("Prenez au moins une photo."); return; }
    setChecking(true); setErr("");
    try {
      const r = await authApi.geoPrecheck({
        city: meta.city, postal_code: meta.postal_code, department: meta.department, transcript,
      });
      if (r && r.status === "confirm") {
        setChecking(false);
        setCityModal({ suggestions: r.suggestions || [], input: r.input || meta.city, detected: !!r.detected_from_transcript });
        return;
      }
      if (r && r.resolved) {
        const c = r.resolved;
        const nm = { city: c.city, postal_code: c.postal_code || meta.postal_code, department: c.department || meta.department, region: c.region || meta.region };
        setMeta((m) => ({ ...m, ...nm }));
        setChecking(false);
        return generate(nm);
      }
    } catch (_) { /* si la validation échoue, on ne bloque pas la génération */ }
    setChecking(false);
    generate();
  };

  const pickCommune = (c) => {
    const nm = { city: c.city, postal_code: c.postal_code || meta.postal_code, department: c.department || meta.department, region: c.region || meta.region };
    setMeta((m) => ({ ...m, ...nm }));
    setCityModal(null);
    generate(nm);
  };
  const keepMyInput = () => { setCityModal(null); generate(); };

  const launchStaging = async () => {
    setStaging("processing");
    try {
      const d = await authApi.autoStaging(result.slug, { style: stStyle, custom_style: customStyle });
      setStaging(`✅ ${d.queued} rendu(s) home staging en cours — retrouvez-les dans l'éditeur.`);
    } catch (e) { setStaging(e.message || "Génération impossible."); }
  };

  const done = captures.filter((c) => c.room && c.note).length;
  const progress = captures.length ? Math.min(95, Math.round((done / Math.max(captures.length, 4)) * 95)) : 0;

  return (
    <div className="min-h-screen bg-[#F1F3F5] text-[#111827] sp-sec" data-testid="visite-ia">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-[#E5E7EB] px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav("/mon-compte")} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-[#111827]" data-testid="visite-back"><ArrowLeft className="w-4 h-4" /> Mon espace</button>
        <span className="calli text-xl text-[#C19A6B]">Visite IA</span>
        <span className="text-[11px] text-zinc-500">{captures.length} photo{captures.length > 1 ? "s" : ""}</span>
      </div>

      {result ? (
        <div className="max-w-lg mx-auto px-5 py-12 text-center space-y-6" data-testid="visite-result">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#C19A6B]/20 border-2 border-[#C19A6B] flex items-center justify-center"><Check className="w-8 h-8 text-[#C19A6B]" /></div>
          <div>
            <h2 className="text-2xl font-light">Votre annonce est prête à ~95 %</h2>
            <p className="text-sm text-zinc-400 mt-2">« {result.listing?.title} » — {result.listing?.images?.length || 0} photos classées. Relisez, complétez si besoin, puis publiez.</p>
          </div>
          {result.cost && (
            <p className="text-[11px] text-zinc-500">Coût IA estimé : {result.cost.estimated_eur} € (plafond {result.cost.budget_eur} €) · modèle {result.cost.model_tier === "economy" ? "économique" : "qualité"} · {result.cost.notes_used}/{result.cost.notes_total} notes analysées</p>
          )}

          {/* Génération complète en 1 clic — moteur identique à Mon Compte */}
          {result.slug && (
            <OneClickGenerate slug={result.slug} images={result.listing?.images || []} defaultIncludeText={true} refineNote />
          )}
          <div className="flex flex-col gap-3">
            <button onClick={() => nav(`/mon-compte?wizard=${result.slug}`)} className="w-full bg-[#C19A6B] hover:bg-[#d1af84] text-white font-semibold uppercase tracking-wider py-3 transition-colors" data-testid="visite-open-wizard">Publier via l'Assistant de publication</button>
            <a href={`/bien/${result.slug}`} target="_blank" rel="noopener noreferrer" className="w-full border border-white/20 text-white/80 hover:text-white text-sm uppercase tracking-wider py-3 inline-flex items-center justify-center gap-2"><Home className="w-4 h-4" /> Prévisualiser</a>
          </div>
          <p className="text-[11px] text-zinc-500">Même parcours que la création par lien ou par formulaire : vérifiez le contenu, les photos et le home staging, puis publiez et partagez.</p>
        </div>
      ) : (
        <div className="max-w-lg mx-auto px-4 py-5 space-y-5 pb-40">
          {/* Infos du bien */}
          <div className="grid grid-cols-2 gap-2">
            <CityAutocomplete
              value={meta.city}
              onSelect={(c) => setMeta((m) => ({ ...m, city: c.city, postal_code: c.postal_code ?? m.postal_code, department: c.department ?? m.department, region: c.region ?? m.region }))}
              placeholder="Ville"
              dark
              testId="visite-city"
              className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]"
            />
            <select value={meta.type} onChange={(e) => setMeta({ ...meta, type: e.target.value })} className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#C19A6B]">
              {["Maison", "Appartement", "Villa", "Terrain", "Immeuble", "Loft"].map((t) => <option key={t} className="bg-white text-[#111827]">{t}</option>)}
            </select>
            <input value={meta.address} onChange={(e) => setMeta({ ...meta, address: e.target.value })} placeholder="Adresse (facultatif)" className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#C19A6B]" />
            <input value={meta.price} onChange={(e) => setMeta({ ...meta, price: e.target.value })} placeholder="Prix (€)" inputMode="numeric" className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#C19A6B]" />
          </div>
          {(meta.postal_code || meta.department) && (
            <p className="-mt-3 text-[11px] text-[#C19A6B]" data-testid="visite-sector">Secteur détecté : {meta.city} {meta.postal_code} {meta.department ? `· ${meta.department}` : ""}</p>
          )}

          {/* Enregistrement continu de la visite (avec consentement RGPD) */}
          <div className="border border-white/10 bg-[#0d0d10] p-4 space-y-3" data-testid="visite-record-block">
            <div className="flex items-start gap-2">
              <input id="rec-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#C19A6B]" data-testid="visite-consent" />
              <label htmlFor="rec-consent" className="text-xs text-zinc-400 leading-snug">J'ai informé les personnes présentes et j'ai leur accord pour enregistrer la visite (RGPD). L'audio sert uniquement à générer l'annonce et n'est pas conservé au-delà de la transcription.</label>
            </div>
            <div className="pt-1">
              <GoldWave active={recording} />
            </div>
            <div className="flex items-center justify-center gap-3">
              {!recording ? (
                <motion.button onClick={startRec} disabled={!consent || transcribing} data-testid="visite-rec-start"
                  animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.08 }}
                  className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider rounded-[5px] px-6 py-3 disabled:opacity-40"
                  style={{ background: "#C19A6B", boxShadow: "0 0 26px rgba(193,154,107,0.55)" }}>
                  {transcribing ? <><Loader2 className="w-5 h-5 animate-spin" /> Transcription…</> : <><Mic className="w-5 h-5" /> Enregistrer la visite</>}
                </motion.button>
              ) : (
                <button onClick={stopRec} data-testid="visite-rec-stop"
                  className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider rounded-[5px] px-6 py-3"
                  style={{ background: "#C19A6B", boxShadow: "0 0 26px rgba(193,154,107,0.55)" }}>
                  <Square className="w-5 h-5" /> Terminer · {fmtDur(recSecs)}
                </button>
              )}
            </div>
            {transcript && (
              <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} className="w-full bg-white/5 border border-white/15 px-3 py-2 text-xs text-zinc-200 min-h-[80px] outline-none focus:border-[#C19A6B]" placeholder="Retranscription de la visite…" data-testid="visite-transcript" />
            )}
            {transcript && <p className="text-[11px] text-zinc-500">L'IA extraira automatiquement les infos utiles (surfaces, année, chauffage, travaux, toiture, isolation, taxe foncière, énergie, équipements, histoire, atouts).</p>}
          </div>

          {/* Boutons photo : caméra + galerie du téléphone */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block cursor-pointer" data-testid="visite-take-photo">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 border-2 border-dashed border-[#C19A6B]/50 bg-[#C19A6B]/5 hover:bg-[#C19A6B]/10 py-6 px-2 transition-colors text-center">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /> : <Camera className="w-6 h-6 text-[#C19A6B]" />}
                <span className="text-xs sm:text-sm font-medium uppercase tracking-wider text-[#C19A6B]">{uploading ? "Envoi…" : "Prendre une photo"}</span>
              </div>
              <input type="file" accept="image/*" capture="environment" multiple onChange={onPhoto} className="hidden" />
            </label>
            <label className="block cursor-pointer" data-testid="visite-gallery-photo">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 border-2 border-dashed border-[#C19A6B]/50 bg-[#C19A6B]/5 hover:bg-[#C19A6B]/10 py-6 px-2 transition-colors text-center">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /> : <ImageIcon className="w-6 h-6 text-[#C19A6B]" />}
                <span className="text-xs sm:text-sm font-medium uppercase tracking-wider text-[#C19A6B]">{uploading ? "Envoi…" : "Choisir dans mes photos"}</span>
              </div>
              <input type="file" accept="image/*" multiple onChange={onPhoto} className="hidden" />
            </label>
          </div>

          {err && <div className="text-sm text-red-400" data-testid="visite-error">{err}{noCredit && <a href="/mon-compte" className="block mt-2 text-[#C19A6B] underline font-semibold" data-testid="visite-buy-credits">Acheter des crédits →</a>}</div>}

          {/* Captures */}
          {captures.length >= 2 && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} data-testid="visite-autosort-badge"
              className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider text-[#C19A6B] bg-[#C19A6B]/10 border border-[#C19A6B]/30 rounded-[5px] px-3 py-2">
              <span className="flex items-center gap-2">
                {autoSort
                  ? <><ListOrdered className="w-3.5 h-3.5" /> Photos triées par pièce à la génération — glissez pour un ordre manuel</>
                  : <><GripVertical className="w-3.5 h-3.5" /> Ordre personnalisé — glissez pour ajuster</>}
              </span>
              {!autoSort && (
                <button onClick={() => { setCaptures((p) => sortByRoom(p)); setAutoSort(true); }} data-testid="visite-reset-sort"
                  className="normal-case tracking-normal text-[#C19A6B] hover:underline inline-flex items-center gap-1 shrink-0"><ListOrdered className="w-3.5 h-3.5" /> Retrier auto</button>
              )}
            </motion.div>
          )}
          <AnimatePresence>
            {captures.map((c, i) => (
              <motion.div key={c.image_url} layout
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ layout: { duration: 0.4, ease: "easeInOut" } }}
                onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
                onDrop={(e) => { e.preventDefault(); dropAt(i); }}
                className={`border bg-[#0d0d10] overflow-hidden transition-colors ${overIdx === i && dragIdx !== null ? "border-[#C19A6B]" : "border-white/10"} ${dragIdx === i ? "opacity-50" : ""}`} data-testid={`visite-capture-${i}`}>
                <div className="relative">
                  <img src={c.image_url} alt="" className="w-full aspect-[16/10] object-cover" draggable={false} />
                  <div draggable onDragStart={() => setDragIdx(i)} onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                    data-testid={`visite-drag-${i}`} title="Glisser pour réordonner"
                    className="absolute top-2 left-2 bg-black/70 text-white p-1.5 rounded-full cursor-grab active:cursor-grabbing"><GripVertical className="w-4 h-4" /></div>
                  <button onClick={() => removeCap(i)} className="absolute top-2 right-2 bg-black/70 text-white p-1.5 rounded-full" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                  {c.room && <span className="absolute bottom-2 left-2 bg-[#C19A6B] text-white text-[11px] font-semibold uppercase px-2 py-1">{c.room === "autre" && c.room_custom ? c.room_custom : roomLabel(c.room)}</span>}
                </div>
                <div className="p-3 space-y-3">
                  <div className="flex flex-wrap gap-1.5" data-testid={`visite-rooms-${i}`}>
                    {ROOMS.map(([rk, rl]) => (
                      <button key={rk} onClick={() => setRoom(i, rk)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${c.room === rk ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-white/20 text-zinc-400 hover:text-white"}`}>{rl}</button>
                    ))}
                  </div>
                  {c.room === "autre" && (
                    <div className="space-y-2">
                      <input value={c.room_custom || ""} onChange={(e) => setRoomCustom(i, e.target.value)} onBlur={(e) => saveCustomRoom(e.target.value)} data-testid={`visite-custom-room-${i}`}
                        placeholder="Précisez la pièce (ex : Salle de jeux, Buanderie, Dressing…)"
                        className="w-full bg-white/5 border border-[#C19A6B]/40 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
                      {customRooms.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid={`visite-custom-room-saved-${i}`}>
                          {customRooms.map((r) => (
                            <button key={r} type="button" onClick={() => setRoomCustom(i, r)}
                              className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${(c.room_custom || "").toLowerCase() === r.toLowerCase() ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-white/20 text-zinc-400 hover:text-white"}`}>{r}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#C19A6B] mb-1.5"><Wand2 className="w-3.5 h-3.5" /> Précision home staging <span className="text-zinc-500 normal-case tracking-normal">(facultatif)</span></p>
                    <VoiceTextBox value={c.staging_note || ""} onChange={(v) => setStagingNote(i, v)} placeholder="Ex : « ajouter un tipi avec des enfants qui jouent », « salon cosy avec cheminée allumée »…" testid={`visite-staging-note-${i}`} minHeight="min-h-[56px]" />
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`visite-staging-suggestions-${i}`}>
                      {suggestionsForRoom(c.room === "autre" ? c.room_custom : c.room).map((s) => (
                        <button key={s} type="button" onClick={() => addStagingSuggestion(i, s)}
                          className="text-[10px] px-2 py-1 rounded-full border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/15 transition-colors">+ {s}</button>
                      ))}
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-1">Appliqué au home staging de cette photo, quel que soit le style. Vide = mise en scène habituelle.</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {captures.length === 0 && (
            <p className="text-sm text-zinc-500 text-center py-6">Lancez la visite : prenez une photo de chaque pièce, indiquez laquelle et dictez un commentaire. L'IA rédigera l'annonce pour vous.</p>
          )}
        </div>
      )}

      {/* Barre de génération fixe */}
      {!result && captures.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-t border-[#E5E7EB] p-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-2">
              <span>Progression estimée</span><span className="text-[#C19A6B] font-semibold">{progress} %</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full mb-3 overflow-hidden"><div className="h-full bg-[#C19A6B] transition-all duration-500" style={{ width: `${progress}%` }} /></div>
            <button onClick={preflight} disabled={generating || checking} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-semibold uppercase tracking-wider py-3.5 transition-colors" data-testid="visite-generate">
              {generating ? <><Loader2 className="w-5 h-5 animate-spin" /> Génération de l'annonce…</> : checking ? <><Loader2 className="w-5 h-5 animate-spin" /> Vérification de la ville…</> : <><Sparkles className="w-5 h-5" /> {genFailed ? "Réessayer la génération" : "Terminer & générer l'annonce"} <ChevronRight className="w-4 h-4" /></>}
            </button>
            {genFailed && !generating && (
              <p className="mt-3 text-center text-xs text-zinc-400" data-testid="visite-gen-help">Vos informations sont conservées. Vous pouvez relire/compléter ci-dessus puis relancer.</p>
            )}
          </div>
        </div>
      )}

      {/* Confirmation phonétique de la commune (avant toute génération IA) */}
      <AnimatePresence>
        {cityModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="visite-city-modal">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              className="w-full max-w-md bg-white text-[#111827] rounded-[8px] shadow-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#C19A6B]/15 flex items-center justify-center"><MapPin className="w-4 h-4 text-[#C19A6B]" /></span>
                <div>
                  <h3 className="text-base font-semibold">Confirmez la commune</h3>
                  <p className="text-[11px] text-zinc-500">Vérification avant la génération de l'annonce (aucun crédit consommé).</p>
                </div>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-sm text-zinc-600 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" />
                  {cityModal.detected
                    ? <>Ville détectée dans la dictée : « <b>{cityModal.input}</b> ». Choisissez la commune officielle correspondante.</>
                    : <>La commune « <b>{cityModal.input}</b> » n'est pas reconnue avec certitude. Sélectionnez la bonne commune :</>}
                </p>
                {cityModal.suggestions.length > 0 ? (
                  <div className="space-y-2 max-h-72 overflow-auto" data-testid="visite-city-suggestions">
                    {cityModal.suggestions.map((c, i) => (
                      <button key={`${c.insee || c.city}-${i}`} type="button" onClick={() => pickCommune(c)}
                        data-testid={`visite-city-option-${i}`}
                        className="w-full text-left px-3 py-2.5 rounded-[6px] border border-zinc-200 hover:border-[#C19A6B] hover:bg-[#C19A6B]/5 transition-colors flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <MapPin className="w-4 h-4 text-[#C19A6B] shrink-0" />
                          <span className="min-w-0">
                            <span className="font-medium block truncate">{c.city}</span>
                            <span className="text-xs text-zinc-500">{c.postal_code} · {c.department}</span>
                          </span>
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#C19A6B] shrink-0">{Math.round((c.confidence || 0) * 100)}%</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 italic" data-testid="visite-city-none">Aucune commune correspondante trouvée. Corrigez la ville ci-dessus ou conservez votre saisie.</p>
                )}
              </div>
              <div className="px-5 py-4 border-t border-zinc-100 flex flex-col sm:flex-row gap-2">
                <button onClick={() => setCityModal(null)} data-testid="visite-city-cancel"
                  className="flex-1 py-2.5 text-sm font-medium border border-zinc-200 rounded-[6px] hover:bg-zinc-50 transition-colors">Corriger la ville</button>
                <button onClick={keepMyInput} data-testid="visite-city-keep"
                  className="flex-1 py-2.5 text-sm font-medium bg-[#111827] text-white rounded-[6px] hover:bg-black transition-colors">Conserver « {cityModal.input || "ma saisie"} »</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
